import { apiClient, toCamelCase, toSnakeCase } from "./client";
import type {
  CreateJobInput,
  ForgeChatMessage,
  ForgeChatTurnResponse,
  ForgeDraftResponse,
  ForgeFormat,
  JobDetail,
  JobSummary,
  ModifierCatalog,
  TemplateForgeConfig,
} from "@/types";

export const forgeApi = {
  // catalogs
  getModifiers: async (): Promise<ModifierCatalog> => {
    const response = await apiClient.get("/forge/modifiers");
    return toCamelCase(response.data) as ModifierCatalog;
  },

  getFormats: async (): Promise<ForgeFormat[]> => {
    const response = await apiClient.get("/forge/formats");
    return toCamelCase(response.data) as ForgeFormat[];
  },

  // template forge config (for wizard)
  getTemplateConfig: async (templateId: number): Promise<TemplateForgeConfig> => {
    const response = await apiClient.get(`/document-templates/${templateId}/forge-config`);
    return toCamelCase(response.data) as TemplateForgeConfig;
  },

  // jobs
  list: async (spaceId: number, limit = 50): Promise<JobSummary[]> => {
    const response = await apiClient.get(`/spaces/${spaceId}/forge`, { params: { limit } });
    return (toCamelCase(response.data) as JobSummary[]) || [];
  },

  get: async (jobId: number): Promise<JobDetail> => {
    const response = await apiClient.get(`/forge/${jobId}`);
    return toCamelCase(response.data) as JobDetail;
  },

  create: async (spaceId: number, body: CreateJobInput): Promise<JobDetail> => {
    const response = await apiClient.post(`/spaces/${spaceId}/forge`, toSnakeCase(body));
    return toCamelCase(response.data) as JobDetail;
  },

  cancel: async (jobId: number): Promise<JobDetail> => {
    const response = await apiClient.post(`/forge/${jobId}/cancel`);
    return toCamelCase(response.data) as JobDetail;
  },

  retry: async (jobId: number): Promise<JobDetail> => {
    const response = await apiClient.post(`/forge/${jobId}/retry`);
    return toCamelCase(response.data) as JobDetail;
  },

  remove: async (jobId: number): Promise<void> => {
    await apiClient.delete(`/forge/${jobId}`);
  },

  // ─── Forja conversacional (F6) ──────────────────────────────────────

  getChatHistory: async (jobId: number): Promise<ForgeChatMessage[]> => {
    const response = await apiClient.get(`/forge/${jobId}/chat`);
    return (toCamelCase(response.data) as ForgeChatMessage[]) || [];
  },

  chat: async (jobId: number, message: string): Promise<ForgeChatTurnResponse> => {
    const response = await apiClient.post(`/forge/${jobId}/chat`, { message });
    return toCamelCase(response.data) as ForgeChatTurnResponse;
  },

  getDraft: async (jobId: number): Promise<ForgeDraftResponse> => {
    const response = await apiClient.get(`/forge/${jobId}/draft`);
    return toCamelCase(response.data) as ForgeDraftResponse;
  },

  exportConversational: async (jobId: number): Promise<JobDetail> => {
    const response = await apiClient.post(`/forge/${jobId}/export`);
    return toCamelCase(response.data) as JobDetail;
  },

  downloadOutput: async (jobId: number, outputId: number): Promise<Blob> => {
    const response = await apiClient.get(`/forge/${jobId}/outputs/${outputId}/download`, {
      responseType: "blob",
    });
    return response.data as Blob;
  },

  extractVariables: async (
    spaceId: number,
    payload: { templateId: number; inputs: Record<string, unknown[]> },
  ): Promise<{
    variables: Record<string, string>;
    sourceEvidence: Record<string, string>;
    tokensIn: number;
    tokensOut: number;
    costEstimateEur: number;
    durationMs: number;
  }> => {
    const response = await apiClient.post(
      `/spaces/${spaceId}/forge/extract-variables`,
      toSnakeCase(payload),
    );
    return toCamelCase(response.data) as {
      variables: Record<string, string>;
      sourceEvidence: Record<string, string>;
      tokensIn: number;
      tokensOut: number;
      costEstimateEur: number;
      durationMs: number;
    };
  },
};
